import React, { useEffect, useMemo, useState } from "react";
import {
  fetchProviderAppointmentCardsByDate,
  ZONES,
  completeAppointment,
  cancelAppointmentProvider,
  markNoShow,
} from "../APIs/appointmentsApi";

// Minimal Card component
function PatientApptCard({ card, onClick, actions }) {
  return (
    <div className="w-full bg-white border rounded-2xl shadow-sm p-4">
      <button
        onClick={onClick}
        className="w-full text-left hover:opacity-90 focus:outline-none"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-lg font-semibold">
              {card.firstName} {card.middleName ? `${card.middleName} ` : ""}
              {card.lastName}
            </div>
            <div className="text-sm text-gray-600">{card.email}</div>
            <div className="text-sm text-gray-600">{card.phoneNumber}</div>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium">Start</div>
            <div className="text-sm">{fmtTime(card.appointmentStart)}</div>
            <div className="text-sm font-medium mt-1">End</div>
            <div className="text-sm">{fmtTime(card.appointmentEnd)}</div>
          </div>
        </div>
      </button>
      {actions ? (
        <div className="mt-3 flex flex-wrap gap-2">{actions}</div>
      ) : null}
    </div>
  );
}

// Simple modal (for click-through UX now; wire to your routes later)
function Modal({ open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl p-6">
        <div className="flex justify-end mb-2">
          <button
            onClick={onClose}
            className="px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200"
          >
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const TAB_DEFS = [
  { key: "scheduled", label: "Scheduled Appointments", states: ["SCHEDULED"] },
  { key: "completed", label: "Completed Appointments", states: ["COMPLETED"] },
  { key: "cancelled", label: "Cancelled Appointments", states: ["CANCELLED"] },
  { key: "noshow", label: "No-Shows", states: ["NOSHOW"] },
  // Per your instruction: Past = SCHEDULED_PAST only (Completed has its own tab)
  { key: "past", label: "Past Appointments", states: ["SCHEDULED_PAST"] },
];

function todayYYYYMMDD(zone) {
  // For simplicity, use browser local today. If you want exact zone-based today,
  // compute server-side or use a timezone lib.
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function fmtTime(zdtString) {
  const isoWithOffset = String(zdtString).split("[")[0];
  const d = new Date(isoWithOffset);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function ProviderAppointmentsTab({ providerId }) {
  //const { providerId: providerIdParam } = useParams(); // assumes route like /providers/:providerId/appointments
  //const providerId = Number(providerIdParam) || Number(sessionStorage.getItem('providerId')) || 0;

  const [activeKey, setActiveKey] = useState("scheduled");
  const [date, setDate] = useState(() => todayYYYYMMDD("America/New_York"));
  const [zone, setZone] = useState("America/New_York");
  const [cardsPage, setCardsPage] = useState(null); // Page JSON
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedCard, setSelectedCard] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  const states = useMemo(
    () => TAB_DEFS.find((t) => t.key === activeKey)?.states || [],
    [activeKey],
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError("");
        const page = await fetchProviderAppointmentCardsByDate({
          providerId,
          date,
          zone,
          states,
          page: 0,
          size: 100,
        });
        if (!cancelled) setCardsPage(page);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (providerId) load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, date, zone, states.join(",")]);

  return (
    <div className="space-y-4">
      <button
        onClick={() => window.history.back()}
        className="mb-4 text-sm px-3 py-1.5 rounded bg-gray-200 hover:bg-gray-300 flex items-center gap-1"
      >
        ← Back
      </button>
      <h3 className="text-xl font-semibold">Appointments</h3>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Date */}
        <div>
          <label className="block mb-1 font-medium">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        {/* Zone */}
        <div>
          <label className="block mb-1 font-medium">Time Zone</label>
          <select
            value={zone}
            onChange={(e) => setZone(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            {ZONES.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {TAB_DEFS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveKey(tab.key)}
            className={`px-3 py-2 rounded-2xl text-sm whitespace-nowrap ${activeKey === tab.key ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-800 hover:bg-gray-200"}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Results */}
      <div className="min-h-[120px]">
        {loading && <div className="text-gray-600">Loading…</div>}
        {error && !loading && <div className="text-red-600">{error}</div>}

        {!loading && !error && (
          <div className="grid gap-3">
            {(cardsPage?.content || []).length === 0 ? (
              <div className="text-gray-500">
                No appointments found for this selection.
              </div>
            ) : (
              cardsPage.content.map((card, idx) => {
                const isScheduledTab = activeKey === "scheduled";
                const haveId = !!card.appointmentId;
                const isPending = pendingId === card.appointmentId;

                const actionButtons =
                  isScheduledTab && haveId ? (
                    <>
                      {/* Complete (green) */}
                      <button
                        className="px-3 py-1.5 rounded-xl text-sm bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                        disabled={isPending}
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            setPendingId(card.appointmentId);
                            await completeAppointment({
                              appointmentId: card.appointmentId,
                              providerId,
                            });
                            setActiveKey("completed"); // triggers refetch via states change
                          } catch (err) {
                            alert(err.message || "Failed to complete");
                          } finally {
                            setPendingId(null);
                          }
                        }}
                      >
                        Complete Appointment
                      </button>

                      {/* Cancel (red) */}
                      <button
                        className="px-3 py-1.5 rounded-xl text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                        disabled={isPending}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm("Cancel this appointment?"))
                            return;
                          const reason = window.prompt(
                            "Enter cancellation reason (required)",
                          );
                          if (!reason || !reason.trim()) {
                            alert("Cancellation reason is required");
                            return;
                          }
                          try {
                            setPendingId(card.appointmentId);
                            await cancelAppointmentProvider({
                              appointmentId: card.appointmentId,
                              providerId,
                              reason: reason.trim(),
                            });
                            setActiveKey("cancelled"); // triggers refetch
                          } catch (err) {
                            alert(err.message || "Failed to cancel");
                          } finally {
                            setPendingId(null);
                          }
                        }}
                      >
                        Cancel Appointment
                      </button>

                      {/* No-show (amber) */}
                      <button
                        className="px-3 py-1.5 rounded-xl text-sm bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
                        disabled={isPending}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm("Mark as No Show?")) return;
                          try {
                            setPendingId(card.appointmentId);
                            await markNoShow({
                              appointmentId: card.appointmentId,
                              providerId,
                            });
                            setActiveKey("noshow"); // triggers refetch
                          } catch (err) {
                            alert(err.message || "Failed to mark no-show");
                          } finally {
                            setPendingId(null);
                          }
                        }}
                      >
                        Mark As No Show
                      </button>
                    </>
                  ) : null;

                return (
                  <PatientApptCard
                    key={card.appointmentId ?? idx} // ✅ stable key
                    card={card}
                    onClick={() => setSelectedCard(card)}
                    actions={actionButtons}
                  />
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Modal on click (replace with navigate to details, if you prefer) */}
      <Modal open={!!selectedCard} onClose={() => setSelectedCard(null)}>
        {selectedCard && (
          <div className="space-y-2">
            <div className="text-lg font-semibold">Patient</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <div className="text-gray-500">First name</div>
              <div>{selectedCard.firstName}</div>
              <div className="text-gray-500">Middle name</div>
              <div>{selectedCard.middleName || "—"}</div>
              <div className="text-gray-500">Last name</div>
              <div>{selectedCard.lastName}</div>
              <div className="text-gray-500">Email</div>
              <div>{selectedCard.email}</div>
              <div className="text-gray-500">Phone</div>
              <div>{selectedCard.phoneNumber}</div>
              <div className="text-gray-500">Start</div>
              <div>{fmtTime(selectedCard.appointmentStart)}</div>
              <div className="text-gray-500">End</div>
              <div>{fmtTime(selectedCard.appointmentEnd)}</div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
